import type { PolymarketBridge, StatusPayload, Book, OrderbookStore } from '../types';
import { subscribe as sessionSubscribe } from '@renderer/api/sessionApi';
import { pm } from '../api/pm';
import { notifications } from '../ui/notifications';
import { extractMarketSlug } from '@shared/slug';

interface DOMElements {
  slugInput: HTMLInputElement | null;
  btn: HTMLButtonElement | null;
  status: HTMLElement | null;
  flashEl: (HTMLElement & { _t?: ReturnType<typeof setTimeout> }) | null;
  assetSelect: HTMLSelectElement | null;
  market: HTMLElement | null;
  marketSelect?: HTMLSelectElement | null;
}

interface CreateSessionControllerParams {
  el: DOMElements;
  api: PolymarketBridge;
  store: OrderbookStore;
  ensurePanel: (assetId: string, name: string) => Book;
  populateTeamMaps: (tokenIds: string[], outcomesById: Record<string, string>) => void;
  syncTeamLabels: () => void;
  loadOrders: () => void;
  loadPositions: () => void;
  reorderBooks: () => void;
  append: (msg: string | Record<string, unknown>) => void;
  loadFunds: () => void;
}

export function createSessionController({ el, api, store, ensurePanel, populateTeamMaps, syncTeamLabels, loadOrders, loadPositions, reorderBooks, append, loadFunds }: CreateSessionControllerParams) {
  function getSlugInputValue(): string { const v = el.slugInput?.value || ''; return String(v).trim(); }
  async function onSubscribeClick(): Promise<void> {
    const btn = el.btn; if (!btn) return; btn.disabled = true;
    try {
      const val = getSlugInputValue(); append({ click: 'subscribe', slug: val });
      const parsed = extractMarketSlug(val); try { append({ subscribe_parsed: { input: val, parsed } }); } catch {}
      try { localStorage.setItem('pm_last_slug', parsed || val); } catch {}
      // If user has already selected a sub-market in the dropdown, subscribe to it immediately
      if (el.marketSelect && el.marketSelect.style.display !== 'none') {
        const chosenNow = el.marketSelect.value && el.marketSelect.value.trim();
        if (chosenNow) {
          try { localStorage.setItem('pm_last_slug', chosenNow); } catch {}
          try { if (el.slugInput) el.slugInput.value = chosenNow; } catch {}
          if (el.status) { el.status.textContent = 'subscribing…'; el.status.className = 'badge'; }
          const res2 = await sessionSubscribe(chosenNow);
          if (!res2 || !res2.ok) throw new Error((res2 && res2.error) || 'Subscribe failed');
          const tokenIds2 = (res2 as any).tokenIds || (res2.data && (res2.data as any).tokenIds) || [];
          append({ subscribeResult: { ok: true, tokenIds: tokenIds2 } });
          notifications.success('Successfully subscribed to market!', 2000);
          return;
        }
      }
      // Defer subscription: if looks like an event slug or Gamma event URL, fetch markets and populate dropdown
      const isGammaEventUrl = /gamma-api\.polymarket\.com\/events\/slug\//i.test(val);
      const isEventLike = isGammaEventUrl || (!/-game\d+/i.test(parsed) && !/game\s*\d+/i.test(parsed));
      if (isEventLike && el.marketSelect) {
        // If dropdown already populated, just prompt to select and return
        if (el.marketSelect.options && el.marketSelect.options.length > 1) {
          el.marketSelect.style.display = '';
          notifications.info('Select a market from the dropdown, then subscribe.', 2500);
          if (el.status) { el.status.textContent = 'choose market'; el.status.className = 'badge'; }
          return;
        }
        if (el.status) { el.status.textContent = 'loading markets…'; el.status.className = 'badge'; }
        const res = await pm.getEventMarkets({ eventSlug: parsed });
        if (!res || !res.ok || !res.data || !Array.isArray(res.data.markets) || res.data.markets.length === 0) {
          notifications.error('No markets found for this event', 2500);
          if (el.status) { el.status.textContent = 'no markets'; el.status.className = 'badge err'; }
          return;
        }
        const list = res.data.markets;
        // Populate dropdown
        el.marketSelect.innerHTML = '';
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = 'Select a market…';
        el.marketSelect.appendChild(placeholder);
        for (const m of list) {
          const opt = document.createElement('option');
          opt.value = m.slug;
          opt.textContent = m.question || m.slug;
          el.marketSelect.appendChild(opt);
        }
        el.marketSelect.style.display = '';
        notifications.info('Select a market from the dropdown, then subscribe.', 2500);
        if (el.status) { el.status.textContent = 'choose market'; el.status.className = 'badge'; }
        return;
      }
      if (el.status) { el.status.textContent = 'subscribing…'; el.status.className = 'badge'; }
      const res = await sessionSubscribe(parsed);
      if (!res || !res.ok) throw new Error((res && res.error) || 'Subscribe failed');
      const tokenIds = (res as any).tokenIds || (res.data && (res.data as any).tokenIds) || [];
      append({ subscribeResult: { ok: true, tokenIds } });
      notifications.success('Successfully subscribed to market!', 2000);
    } catch (e) { const error = e as Error; const msg = 'Subscribe failed: ' + (error?.message ?? e); append({ subscribeError: msg }); }
    finally { btn.disabled = false; }
  }
  function wireStatus(): void {
    api.onStatus((payload: StatusPayload) => {
      if (payload?.type !== 'debug') append({ onStatus: payload?.type });
      if (payload.type === 'subscribed') {
        if (el.status) { el.status.textContent = 'subscribed'; el.status.className = 'badge ok'; }
        try { store.outcomesById = payload.outcomesById || {}; } catch {}
        try { store.obLastBookTs = 0; store.obLastRenderTs = 0; store.obLastMessageTs = 0; store.renderAttempts = 0; store.renderFailures = 0; store.renderSuccesses = 0; store.obLastRenderError = null; store.pendingEvents = []; } catch {}
        if (el.assetSelect) el.assetSelect.innerHTML = '';
        store.currentAssetIds = payload.tokenIds ? payload.tokenIds.slice() : [];
        for (const id of (payload.tokenIds || [])) { const name = payload.outcomesById?.[id] || id.slice(0, 8); ensurePanel(id, name || id.slice(0, 8)); const opt = document.createElement('option'); opt.value = id; opt.textContent = `${name} (${id.slice(0, 8)}…)`; el.assetSelect?.appendChild(opt); }
        try { populateTeamMaps(payload.tokenIds || [], payload.outcomesById || {}); } catch {}
        syncTeamLabels();
        try { const decimalsById = payload.decimalsById || {}; store.priceDecimalsById = decimalsById; } catch {}
        try { if (typeof reorderBooks === 'function') reorderBooks(); } catch {}
        loadOrders(); loadPositions();
      } else if (payload.type === 'market') {
        if (el.market) el.market.textContent = `${payload.question} (${payload.slug})`;
        try { store.currentSlug = payload.slug || store.currentSlug || ''; } catch {}
        store.latestConditionId = payload.conditionId || store.latestConditionId;
        try { store.priceDecimalsById = payload.decimalsById || {}; } catch {}
      } else if (payload.type === 'auth') {
        const msg = payload.ok ? `Authenticated: ${payload.address}` : `Auth error: ${payload.message}`; append(msg); if (payload.ok) { loadFunds(); }
      } else if (payload.type === 'closed') {
        // Keep UI steady; show closed only if we don't immediately resubscribe
        if (el.status) { el.status.textContent = 'closed'; el.status.className = 'badge'; }
      } else if (payload.type === 'error') {
        if (el.status) { el.status.textContent = 'error'; el.status.className = 'badge err'; }
      } else if (payload.type === 'debug') { append(payload?.message ? payload.message : 'debug'); }
    });
  }
  return { onSubscribeClick, wireStatus };
}
